import { formatNumber } from '@utils/Format'
import {
  CREDITOS_NAO_DEBITADO,
  CREDITOS_SUBCENTAVO,
} from '@modules/ai-framework/constants/copy'

// Exibição de um valor em créditos — UMA regra, um lugar (spec-003, US-1).
//
// ★ POR QUE ISTO É UM UTIL, E NÃO DUAS FUNÇÕES LOCAIS
//
// A regra vivia em duas cópias: `formatCreditos` na coluna Créditos do log
// (`views/executions.vue`) e `valorExibivel` na lista de movimentações
// (`components/creditos-extrato-lista`). Elas JÁ contavam histórias diferentes sobre o
// mesmo débito: para um `creditos` não numérico, a coluna devolvia "não debitado" e a
// lista devolvia `formatNumber(0, 2)` — que saía na tela como `+ 0,00`, exatamente o
// zero que este projeto existe para não exibir. É o mesmo motivo pelo qual a constante
// `CREDITOS_SUBCENTAVO` já tinha sido extraída para `constants/copy` ("em duas cópias,
// uma muda sozinha"); só a função tinha ficado para trás.
//
// AS TRÊS DECISÕES QUE A FUNÇÃO CARREGA
//
// 1. **Ausência nunca vira zero.** `null`, `undefined`, `''` e qualquer coisa que não
//    seja número finito são NÃO DEBITADO (ADR-011). `0,00` se lê como "grátis" — é o
//    risco nº 1 do projeto, "saldo que mente por omissão".
// 2. **Débito abaixo de um centésimo é `< 0,01`, não `0,00`.** A cobrança existiu; o
//    que não cabe são as casas decimais.
// 3. **Zero VINDO DO SERVIDOR é dado legítimo** e sai como `0,00`: é a diferença entre
//    "o lançamento vale zero" e "não há lançamento".
const CASAS_EXIBICAO = 2
// Abaixo desta régua o débito existiu e não cabe em 2 casas.
const CREDITO_MINIMO_EXIBIVEL = 0.01

// Devolve `{ label, moduloLabel, entrada, naoDebitado }`:
//
// - `label`      — o valor COM sinal aritmético (`-0,43`), para a coluna do log;
// - `moduloLabel`— a grandeza sem sinal (`0,43`), para quem exibe o sinal em símbolo
//                  próprio (a lista, onde `+`/`−` são um canal de leitura à parte);
// - `entrada`    — `true` quando o valor é `>= 0`. Zero é entrada neutra, não uma saída
//                  inventada. Sem significado quando `naoDebitado` é `true`;
// - `naoDebitado`— o lançamento não tem valor numérico; ambos os rótulos são a copy.
function formatCreditosExibicao(bruto) {
  if (bruto === undefined || bruto === null || bruto === '') {
    return naoDebitado()
  }

  const numero = Number(bruto)
  if (!isFinite(numero)) return naoDebitado()

  const modulo = Math.abs(numero)
  if (modulo > 0 && modulo < CREDITO_MINIMO_EXIBIVEL) {
    // Sub-centavo: os dois rótulos são o mesmo marcador — a grandeza é o que não cabe,
    // e o sinal continua viajando em `entrada`.
    return {
      label: CREDITOS_SUBCENTAVO,
      moduloLabel: CREDITOS_SUBCENTAVO,
      entrada: numero >= 0,
      naoDebitado: false,
    }
  }

  return {
    label: formatNumber(numero, CASAS_EXIBICAO),
    moduloLabel: formatNumber(modulo, CASAS_EXIBICAO),
    entrada: numero >= 0,
    naoDebitado: false,
  }
}

function naoDebitado() {
  return {
    label: CREDITOS_NAO_DEBITADO,
    moduloLabel: CREDITOS_NAO_DEBITADO,
    entrada: false,
    naoDebitado: true,
  }
}

// Número exibível ou `null`. `null` NÃO é zero: sem número confirmado o card mostra o
// estado indisponível — `0,00` afirmaria "você não consumiu nada". É a mesma régua de
// ausência de `formatCreditosExibicao`, para quem precisa do NÚMERO (KPIs, média,
// projeção) e não do rótulo. Vivia em duas cópias (`creditos-kpi-strip`, `creditos-uso`).
function numeroOuNulo(bruto) {
  if (bruto === undefined || bruto === null || bruto === '') return null
  const numero = Number(bruto)
  return isFinite(numero) ? numero : null
}

// ★ `CREDITO_MINIMO_EXIBIVEL` NÃO é exportada (S23, review PR #2211): é regra INTERNA
// deste formatador (o limiar de "< 0,01"), sem consumidor fora daqui. Exportar uma
// constante sem chamador cria uma segunda "fonte única" que ninguém confere — se um dia
// outro módulo precisar do mesmo limiar, é sinal de que a regra deveria migrar para cá
// como função, não como número solto para reimplementar a comparação em outro lugar.
// ── Nível do saldo: esgotado / baixo (14/09/2026) ──────────────────────────────
//
// Duas réguas, e são réguas DIFERENTES:
//
// - `saldoEsgotado`: `<= 0` — a MESMA do gate do backend
//   (`aawz_intelligence_v2/core/gate_creditos.py::SALDO_MINIMO_PARA_PERGUNTAR`), que
//   recusa a próxima pergunta com `status: 402`. É o BLOQUEIO: faixa no composer, envio
//   travado, bolha com a porta para os créditos.
// - `saldoBaixo`: `0 < saldo <= LIMIAR_SALDO_BAIXO` — o AVISO antes de acabar: linha do
//   hint do composer e o triângulo no rodapé da lateral. Zero e negativo NÃO são "baixo",
//   são esgotado — quem quer os dois juntos (o rodapé) soma as duas funções.
//
// `LIMIAR_SALDO_BAIXO` em créditos (1 crédito = R$ 1,00). Só o limiar: a estimativa
// "restam ~N perguntas" (custo médio por pergunta) existiu por algumas horas em
// 14/09/2026 e SAIU a pedido do usuário — chamava atenção demais para um aviso que
// deve ser sutil. Não reintroduza sem pedido. Era 20 → 10 → 5 (15/09/2026, pedido
// do usuário: a faixa "acabando" só com 5 ou menos).
const LIMIAR_SALDO_BAIXO = 5

function saldoEsgotado(bruto) {
  const numero = numeroOuNulo(bruto)
  return numero !== null && numero <= 0
}

function saldoBaixo(bruto) {
  const numero = numeroOuNulo(bruto)
  return numero !== null && numero > 0 && numero <= LIMIAR_SALDO_BAIXO
}

export {
  formatCreditosExibicao,
  numeroOuNulo,
  CASAS_EXIBICAO,
  saldoEsgotado,
  saldoBaixo,
}
